import { Cross2Icon } from '@radix-ui/react-icons'
import { Link } from '@tanstack/react-router'
import { Table } from '@tanstack/react-table'
import { Plus, RefreshCw } from 'lucide-react'
import { useTranslation } from '@/hooks/use-translation'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useFnsMode } from '@/features/forms-and-surveys/hooks'

interface DataTableToolbarProps<TData> {
  table: Table<TData>
  refetch: () => Promise<unknown>
  refetching: boolean
}

export function DataTableToolbar<TData>({
  table,
  refetch,
  refetching,
}: DataTableToolbarProps<TData>) {
  const mode = useFnsMode()
  const { t } = useTranslation()
  const isFiltered = table.getState().columnFilters.length > 0

  const handleRefresh = async () => {
    try {
      await refetch()
    } catch {
      // Error is already handled in the hook
    }
  }

  return (
    <div className='flex items-center justify-between'>
      <div className='flex flex-1 items-center space-x-2'>
        <Input
          placeholder={t('surveys.filterResponses')}
          value={(table.getColumn('title')?.getFilterValue() as string) ?? ''}
          onChange={(event) =>
            table.getColumn('title')?.setFilterValue(event.target.value)
          }
          className='h-8 w-[150px] lg:w-[250px]'
        />
        {isFiltered && (
          <Button
            variant='ghost'
            onClick={() => table.resetColumnFilters()}
            className='h-8 px-2 lg:px-3'
          >
            {t('common.reset')}
            <Cross2Icon className='h-4 w-4' />
          </Button>
        )}
      </div>
      <div className='flex items-center space-x-2'>
        <Button
          variant='outline'
          size='sm'
          onClick={handleRefresh}
          className='h-8 px-2 lg:px-3'
          disabled={refetching}
        >
          <RefreshCw className='h-4 w-4' />
          {t('common.refresh')}
        </Button>
        <Button size='sm' className='h-8 px-2 lg:px-3' asChild>
          {mode === 'survey' ? (
            <Link to='/surveys/create'>
              <Plus className='h-4 w-4' />
              {t('surveys.createSurvey')}
            </Link>
          ) : (
            <Link to='/forms/create'>
              <Plus className='h-4 w-4' />
              {t('forms.createForm')}
            </Link>
          )}
        </Button>
      </div>
    </div>
  )
}
